/** Đăng nhập, đăng xuất, đổi mật khẩu, gắn thiết bị. */

import {
  KHOA_NHAN_VIEN, datRefresh, datToken, layRefresh, request, xoaPhienDangNhap,
} from '../api/client'
import type { DangNhapOut, NhanVien } from '../types'

export async function dangNhap(
  code: string, password: string, device_id: string,
): Promise<DangNhapOut> {
  const kq = await request<DangNhapOut>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      code, password, device_id,
      platform: navigator.userAgent.slice(0, 120),
    }),
  })
  datToken(kq.access_token)
  datRefresh(kq.refresh_token)
  localStorage.setItem(KHOA_NHAN_VIEN, JSON.stringify(kq.employee))
  return kq
}

export function nhanVienHienTai(): NhanVien | null {
  const s = localStorage.getItem(KHOA_NHAN_VIEN)
  if (!s) return null
  try {
    return JSON.parse(s) as NhanVien
  } catch {
    // localStorage hỏng thì coi như chưa đăng nhập — đừng để một JSON hỏng
    // làm trắng cả ứng dụng ngay lúc nhân viên cần bấm chấm công.
    localStorage.removeItem(KHOA_NHAN_VIEN)
    return null
  }
}

/** Ghi lại hồ sơ đã lưu — dùng sau khi đổi mật khẩu xong để tắt cờ bắt đổi. */
export function luuNhanVien(nv: NhanVien): void {
  localStorage.setItem(KHOA_NHAN_VIEN, JSON.stringify(nv))
}

export interface KetQuaDoiMatKhau {
  ok: boolean
  access_token: string
  refresh_token: string
  expires_in: number
}

/**
 * Tự đổi mật khẩu — docs/14 §14.3 B6.
 *
 * Gọi được CẢ KHI tài khoản đang bị chặn vì chưa đổi mật khẩu: đây là endpoint
 * duy nhất server chừa lại cho trường hợp đó.
 *
 * Lưu token mới NGAY: token cũ còn mang cờ bắt đổi nên mọi lời gọi sau đó vẫn
 * bị chặn nếu không thay.
 */
export async function doiMatKhau(
  mat_khau_cu: string, mat_khau_moi: string,
): Promise<KetQuaDoiMatKhau> {
  const kq = await request<KetQuaDoiMatKhau>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ mat_khau_cu, mat_khau_moi }),
  })
  datToken(kq.access_token)
  datRefresh(kq.refresh_token)
  const nv = nhanVienHienTai()
  if (nv) luuNhanVien({ ...nv, must_change_password: false })
  return kq
}

export async function dangXuat(): Promise<void> {
  const rt = layRefresh()
  // Dọn phía client trong `finally`: mạng hỏng giữa chừng thì người dùng vẫn
  // thoát được. Kẹt ở trạng thái "đã bấm đăng xuất mà vẫn đăng nhập" nguy
  // hiểm hơn một refresh token còn sống thêm vài ngày.
  //
  // ⚠️ KHÔNG đụng vào hàng đợi offline (`lib/queue.ts`). Trong đó có thể đang
  // nằm giờ công thật chưa gửi được.
  try {
    if (rt) {
      await request('/auth/logout', {
        method: 'POST', body: JSON.stringify({ refresh_token: rt }),
      })
    }
  } finally {
    xoaPhienDangNhap()
  }
}

/**
 * Xin gắn điện thoại này làm thiết bị chấm công.
 *
 * `pending: true` nghĩa là nhân viên đã có máy khác và cần quản lý duyệt đổi;
 * sau khi đổi, hệ thống khoá đổi tiếp 90 ngày (docs/02 §2.7) — đó là cơ chế
 * chống "mượn máy luân phiên", không phải sự phiền hà vô cớ.
 */
export const xinGanThietBi = (device_id: string, reason: string) =>
  request<{ pending: boolean }>('/auth/bind-device', {
    method: 'POST',
    body: JSON.stringify({
      device_id, reason,
      platform: navigator.userAgent.slice(0, 120),
    }),
  })
